import { type User } from '@auth0/auth0-react'
import _ from 'lodash'

import type { UserSettings } from 'models/user-settings'
import { AcceptedWorkspaceNotice } from 'openapi/models/AcceptedWorkspaceNotice'
import { DefaultAdminPermission } from 'openapi/models/DefaultAdminPermission'
import { DefaultBasePermission } from 'openapi/models/DefaultBasePermission'
import { IntegrationPermission } from 'openapi/models/IntegrationPermission'
import { InternalOnlyPermission } from 'openapi/models/InternalOnlyPermission'
import { Permission as OpenApiPermission } from 'openapi/models/Permission'
import { RestrictedPermission } from 'openapi/models/RestrictedPermission'
import { SensitiveCustomerDataPermission } from 'openapi/models/SensitiveCustomerDataPermission'
import { SensitiveDataPermGranterEmail } from 'openapi/models/SensitiveDataPermGranterEmail'
import { SensitiveUserPermission } from 'openapi/models/SensitiveUserPermission'
import { UserInfoUserProfile } from 'openapi/models/UserInfoUserProfile'
import { WorkspaceEyesOffTier } from 'openapi/models/WorkspaceEyesOffTier'
import { WorkspaceFeature } from 'openapi/models/WorkspaceFeature'
import Services from 'services'
import { Maybe } from 'types'

import { TaskType } from 'utils/task'

import { useUserProfileStore } from 'components/common/user-profile-store'

import { PermWithSource } from './perms'
import {
  WelcomeScreenType,
  Workspace,
  WorkspaceKind,
  type RawWorkspace,
  type WelcomeScreen,
} from './workspace'

export const Permission: Record<string, OpenApiPermission> = {
  BASE: OpenApiPermission.CREATE_COMPLETION,
  ORGANIZATION_ADMIN: OpenApiPermission.ADMIN_ORGANIZATION,
  TERRITORY_ADMIN: OpenApiPermission.ADMIN_TERRITORY,
  INTERNAL_ADMIN_READ: OpenApiPermission.READ_ALL_WORKSPACES,
  INTERNAL_ADMIN_WRITE: OpenApiPermission.WRITE_ALL_WORKSPACES,
  DOCUMENT_QA: OpenApiPermission.CREATE_DOCUMENT_QA,
  MULTI_DOC_QA: OpenApiPermission.CREATE_MULTI_DOC_QA,
  CORPUS_QA: OpenApiPermission.CREATE_CORPUS_QA,
  REDLINES: OpenApiPermission.CREATE_REDLINES,
  SEC_EDGAR_QA: OpenApiPermission.CREATE_SEC_EDGAR_QA,
  SEC_EDGAR_ALL: OpenApiPermission.CREATE_EDGAR_ALL_FILINGS,
  HMRC_QA: OpenApiPermission.CREATE_HMRC_QA,
  JAPAN_TAX_QA: OpenApiPermission.CREATE_JAPAN_TAX_QA,
  NETHERLANDS_TAX_QA: OpenApiPermission.CREATE_NETHERLANDS_TAX_QA,
  INDIA_TAX_QA: OpenApiPermission.CREATE_INDIA_TAX_QA,
  IRELAND_TAX_QA: OpenApiPermission.CREATE_IRELAND_TAX_QA,
  AUSTRALIA_TAX_QA: OpenApiPermission.CREATE_AUSTRALIA_TAX_QA,
  SWEDEN_TAX_QA: OpenApiPermission.CREATE_SWEDEN_TAX_QA,
  KOREA_TAX_QA: OpenApiPermission.CREATE_KOREA_TAX_QA,
  ITALY_TAX_QA: OpenApiPermission.CREATE_ITALY_TAX_QA,
  HONG_KONG_TAX_QA: OpenApiPermission.CREATE_HONG_KONG_TAX_QA,
  KSA_TAX_QA: OpenApiPermission.CREATE_KSA_TAX_QA,
  UAE_TAX_QA: OpenApiPermission.CREATE_UAE_TAX_QA,
  SWITZERLAND_TAX_QA: OpenApiPermission.CREATE_SWITZERLAND_TAX_QA,
  PILLAR_TWO_TAX_QA: OpenApiPermission.CREATE_PILLAR_TWO_TAX_QA,
  MEXICO_TAX_QA: OpenApiPermission.CREATE_MEXICO_TAX_QA,
  WWTS_QA: OpenApiPermission.CREATE_WWTS_QA,
  FEEDBACK: OpenApiPermission.CREATE_FEEDBACK,
  TRANSLATION: OpenApiPermission.CREATE_TRANSLATION,
  TRANSLATION_API: OpenApiPermission.CREATE_TRANSLATION_API,
  COMPANY_PROFILE: OpenApiPermission.CREATE_COMPANY_PROFILE,
  HISTORY: OpenApiPermission.READ_HISTORY,
  EXAMPLES_READ: OpenApiPermission.READ_EXAMPLES,
  EXAMPLES_WRITE: OpenApiPermission.WRITE_EXAMPLES,
  INTERNET_BROWSING: OpenApiPermission.CREATE_INTERNET_BROWSING,
  YOU_WEB_SEARCH: OpenApiPermission.USE_YOU_WEB_SEARCH,
  OGC_REVIEW: OpenApiPermission.CREATE_OGC_REVIEW,
  MANAGE_USERS: OpenApiPermission.MANAGE_USERS,
  MANAGE_BULK_PERMS: OpenApiPermission.MANAGE_BULK_PERMS,
  READ_STATUS_BAR: OpenApiPermission.READ_STATUS_BAR,
  DRAFTING: OpenApiPermission.CREATE_DRAFTING,
  RESPONSE_COMPARE_USER: OpenApiPermission.DECIDE_RESPONSE_COMPARISON,
  RESPONSE_COMPARE_ADMIN: OpenApiPermission.CREATE_RESPONSE_COMPARISON,
  DOC_COMPARE: OpenApiPermission.DOCUMENT_COMPARISON,
  PWC_TAX_FEEDBACK_EXPORT: OpenApiPermission.CREATE_PWC_TAX_FEEDBACK_EXPORT,
  MEMOS_QA: OpenApiPermission.CREATE_MEMOS_QA,
  CONTRACTS: OpenApiPermission.CREATE_CONTRACTS,
  EURLEX_QA: OpenApiPermission.CREATE_EURLEX_QA,
  FRANCE_CASE_LAW: OpenApiPermission.CREATE_FRANCE_CASE_LAW,
  CLIENT_MATTERS_READ: OpenApiPermission.READ_CLIENT_MATTERS,
  CLIENT_MATTERS_WRITE: OpenApiPermission.WRITE_CLIENT_MATTERS,
  VIEW_TOP_CLIENT_MATTERS: OpenApiPermission.VIEW_TOP_CLIENT_MATTERS,
  TRANSCRIPTS: OpenApiPermission.TRANSCRIPTS,
  AUS_BREACH_REPORTING: OpenApiPermission.AUS_BREACH_REPORTING,
  CUATRECASAS: OpenApiPermission.CREATE_CUATRECASAS,
  FROM_COUNSEL: OpenApiPermission.CREATE_FROM_COUNSEL,
  READ_SENSITIVE_DATA: OpenApiPermission.READ_ALL_SENSITIVE,
  ADVANCED_DOC_QA: OpenApiPermission.CREATE_ADVANCED_DOC_QA,
  ASSISTANT_DRAFT: OpenApiPermission.CREATE_ASSISTANT_DRAFT,
  ASSISTANT_V2_SURVEY_FEEDBACK:
    OpenApiPermission.CREATE_ASSISTANT_V2_SURVEY_FEEDBACK,
  LEGACY_OPEN_ENDED: OpenApiPermission.LEGACY_OPEN_ENDED,
  OPEN_ENDED_BRIDGE_ROUTING: OpenApiPermission.USE_OPEN_ENDED_BRIDGE_ROUTING,
  PWC_DEALS: OpenApiPermission.PWC_DEALS,
  DILIGENCE_TRANSCRIPTS: OpenApiPermission.CREATE_DILIGENCE_TRANSCRIPTS,
  DIARIZED_TRANSCRIPTION: OpenApiPermission.USE_DIARIZED_TRANSCRIPTION,
  DEALS_DISCOVERY_ASYNC: OpenApiPermission.USE_DEALS_DISCOVERY_ASYNC_IMPL,
  DEALS_DISCOVERY_FROM_VAULT: OpenApiPermission.USE_DEALS_DISCOVERY_FROM_VAULT,
  PWC_HR_WORKFORCE_API: OpenApiPermission.CREATE_PWC_HR_WORKFORCE_API,
  VAULT_CREATE: OpenApiPermission.CREATE_VAULT,
  VAULT_INTERNAL_ONLY_CREATE: OpenApiPermission.CREATE_VAULT_INTERNAL_ONLY,
  VAULT_REVIEW_VERIFICATION: OpenApiPermission.CREATE_VAULT_REVIEW_VERIFICATION,
  VAULT_SANDBOX_PROJECT_CREATE: OpenApiPermission.CREATE_VAULT_SANDBOX_PROJECT,
  VAULT_WORKFLOW_LOAN_PORTFOLIO_REVIEW:
    OpenApiPermission.CREATE_VAULT_WORKFLOW_LOAN_PORTFOLIO_REVIEW,
  VAULT_WORKFLOW_REPS_WARRANTIES:
    OpenApiPermission.CREATE_VAULT_WORKFLOW_REPS_WARRANTIES,
  VAULT_WORKFLOW_ADMIN: OpenApiPermission.CREATE_VAULT_WORKFLOW_ADMIN,
  VAULT_PROJECT_CLIENT_MATTER:
    OpenApiPermission.USE_VAULT_PROJECT_CLIENT_MATTER,
  LIBRARY_PROMPTS: OpenApiPermission.CREATE_USER_LIBRARY_ITEMS,
  LIBRARY_ADMIN: OpenApiPermission.CREATE_WORKSPACE_LIBRARY_ITEMS,
  LIBRARY_PRIVATE_PROMPTS: OpenApiPermission.CREATE_PRIVATE_LIBRARY_PROMPTS,
  LIBRARY_CREATE_HARVEY_ITEMS: OpenApiPermission.CREATE_HARVEY_LIBRARY_ITEMS,
  VIEWTOUR_CLIENT_MATTER: OpenApiPermission.VIEW_TOUR_CLIENT_MATTER,
  LIBRARY_EXAMPLES: OpenApiPermission.USE_LIBRARY_EXAMPLES,
  SKIP_RATE_LIMIT: OpenApiPermission.USE_SKIP_RATE_LIMIT,
  READ_WORKSPACE_HISTORY: OpenApiPermission.READ_WORKSPACE_HISTORY,
  CLIENT_ADMIN_ADD_USERS: OpenApiPermission.CLIENT_ADMIN_ADD_USERS,
  CLIENT_ADMIN_REMOVE_USERS: OpenApiPermission.CLIENT_ADMIN_REMOVE_USERS,
  CLIENT_ADMIN_VIEW_USERS: OpenApiPermission.CLIENT_ADMIN_VIEW_USERS,
  CLIENT_ADMIN_VAULT_ADD_USERS: OpenApiPermission.CLIENT_ADMIN_VAULT_ADD_USERS,
  CLIENT_ADMIN_VAULT_REMOVE_USERS:
    OpenApiPermission.CLIENT_ADMIN_VAULT_REMOVE_USERS,
  ICERTIS_FIELD_EXTRACTION_API:
    OpenApiPermission.CREATE_ICERTIS_FIELD_EXTRACTION_API,
  VIEW_USAGE_DASHBOARD: OpenApiPermission.VIEW_USAGE_DASHBOARD,
  VIEW_USAGE_DASHBOARD_V2: OpenApiPermission.VIEW_USAGE_DASHBOARD_V2,
  VIEW_USER_INSPECTOR: OpenApiPermission.VIEW_USER_INSPECTOR,
  VIEW_EXPORT_TIMEZONE_SELECTION:
    OpenApiPermission.VIEW_EXPORT_TIMEZONE_SELECTION,
  VIEW_WORKSPACE_INFO: OpenApiPermission.VIEW_WORKSPACE_INFO,
  DELETE_USER_HISTORY: OpenApiPermission.DELETE_USER_HISTORY,
  DELETE_WORKSPACE_HISTORY: OpenApiPermission.DELETE_WORKSPACE_HISTORY,
  DELETE_WORKSPACE_HISTORY_BULK:
    OpenApiPermission.DELETE_WORKSPACE_HISTORY_BULK,
  OPEN_ENDED_BRIDGE_ROUTING_FULL:
    OpenApiPermission.USE_OPEN_ENDED_BRIDGE_ROUTING_FULL,
  READ_WORKSPACE_VAULT: OpenApiPermission.READ_WORKSPACE_VAULT,
  USE_ASSISTANT_V2: OpenApiPermission.USE_ASSISTANT_V2,
  USE_HARVEY_FOR_WORD: OpenApiPermission.USE_HARVEY_FOR_WORD,
  USE_HARVEY_FOR_COPILOT: OpenApiPermission.USE_HARVEY_FOR_COPILOT,
  USE_HARVEY_FOR_CUSTOM_ENGINE_COPILOT:
    OpenApiPermission.USE_HARVEY_FOR_CUSTOM_ENGINE_COPILOT,
  USE_COMPETITIVE_ANALYSIS: OpenApiPermission.USE_COMPETITIVE_ANALYSIS,
  USE_SWEDEN_CASE_LAW: OpenApiPermission.USE_SWEDEN_CASE_LAW,
  USE_SINGAPORE_CASE_LAW: OpenApiPermission.USE_SINGAPORE_CASE_LAW,
  USE_MACFARLANES_KS: OpenApiPermission.USE_MACFARLANES_KS,
  USE_LEFEBVRE_KS: OpenApiPermission.USE_LEFEBVRE_KS,
  USE_BREDIN_PRAT_KS: OpenApiPermission.USE_BREDIN_PRAT_KS,
  READ_ENG_INTERNAL: OpenApiPermission.READ_ENG_INTERNAL,
  WRITE_ENG_INTERNAL: OpenApiPermission.WRITE_ENG_INTERNAL,
  USE_VAULT_SHARING: OpenApiPermission.USE_VAULT_SHARING,
  USE_EVENT_SHARING: OpenApiPermission.USE_EVENT_SHARING,
  CREATE_VAULT_SHARES: OpenApiPermission.CREATE_VAULT_SHARES,
  CREATE_EVENT_SHARES: OpenApiPermission.CREATE_EVENT_SHARES,
  MANAGE_SHARING: OpenApiPermission.CLIENT_ADMIN_MANAGE_SHARING,
  VIEW_HELP_CENTER: OpenApiPermission.VIEW_HELP_CENTER,
  READ_SOME_SENSITIVE_DATA: OpenApiPermission.READ_SOME_SENSITIVE_DATA,
  DILIGENCE_UK_TAX: OpenApiPermission.CREATE_DILIGENCE_UK_TAX,
  ICERTIS_REVIEW_API: OpenApiPermission.CREATE_ICERTIS_REVIEW_API,
  CREATE_VALS_API: OpenApiPermission.CREATE_VALS_API,
  ASYNC_FIELD_EXTRACTION_API:
    OpenApiPermission.CREATE_ASYNC_FIELD_EXTRACTION_API,
  ASSISTANT_TAX_KNOWLEDGE_SOURCE: OpenApiPermission.USE_ASSISTANT_TAX_KS,
  DISALLOW_CLIENT_MATTERS: OpenApiPermission.USE_DISALLOWED_CLIENT_MATTERS,
  ASSISTANT_RESEARCH_KNOWLEDGE_SOURCE:
    OpenApiPermission.USE_ASSISTANT_RESEARCH_KS,
  CLIENT_ADMIN_API_TOKEN_MGMT: OpenApiPermission.CLIENT_ADMIN_API_TOKEN_MGMT,
  READ_MESSAGE_LEVEL_CREATED_TIMESTAMP:
    OpenApiPermission.READ_MESSAGE_LEVEL_CREATED_TIMESTAMP,
  SHAREPOINT_INTEGRATION: OpenApiPermission.USE_SHAREPOINT_INTEGRATION,
  GOOGLE_DRIVE_INTEGRATION: OpenApiPermission.USE_GOOGLE_DRIVE_INTEGRATION,
  IMANAGE_INTEGRATION: OpenApiPermission.USE_IMANAGE_INTEGRATION,
  INTEGRATION_ADMIN: OpenApiPermission.CLIENT_ADMIN_INTEGRATIONS,
  CREATE_USER_PROFILES: OpenApiPermission.CREATE_USER_PROFILES,
  CREATE_USER_PROFILES_OPTIONAL:
    OpenApiPermission.CREATE_USER_PROFILES_OPTIONAL,
  CREATE_USER_PROFILES_OPTIONAL_STEPS:
    OpenApiPermission.CREATE_USER_PROFILES_OPTIONAL_STEPS,
  VIEW_GDRIVE_ANNOUNCEMENT: OpenApiPermission.VIEW_GDRIVE_ANNOUNCEMENT,
  VIEW_WORD_ADD_IN_ANNOUNCEMENT:
    OpenApiPermission.VIEW_WORD_ADD_IN_ANNOUNCEMENT,
  USE_ASSISTANT_LOCALIZATION: OpenApiPermission.USE_ASSISTANT_LOCALIZATION,
  USE_PASSWORD_PROTECTED_DOCS: OpenApiPermission.USE_PASSWORD_PROTECTED_DOCS,
  USE_DISCOVER_TAB: OpenApiPermission.USE_DISCOVER_TAB,
  USE_PERMISSION_BUNDLES: OpenApiPermission.USE_PERMISSION_BUNDLES,
  USE_NETDOCS_EXTENSION: OpenApiPermission.USE_NETDOCS_EXTENSION,
  USE_DMS_ONE_WAY_SYNC: OpenApiPermission.USE_DMS_ONE_WAY_SYNC,
  CLIENT_ADMIN_MANAGE_ASSISTANT_WORKFLOWS:
    OpenApiPermission.CLIENT_ADMIN_MANAGE_ASSISTANT_WORKFLOWS,
  // == Workflows Permissions ==
  USE_TRANSCRIBE_AUDIO_TO_TEXT: OpenApiPermission.USE_TRANSCRIBE_AUDIO_TO_TEXT,
  // == End Workflow Permissions ==
  DOC_PROCESSING_RPC: OpenApiPermission.USE_DOC_PROCESSING_RPC,
  LANCE_ENTERPRISE_VAULT: OpenApiPermission.USE_LANCE_ENTERPRISE_VAULT,
  PLAYBOOK_REVIEW: OpenApiPermission.CREATE_PLAYBOOK_REVIEW,
  USE_ASSISTANT_COMPOSER: OpenApiPermission.USE_ASSISTANT_COMPOSER,
  CALL_SUMMARIZATION: OpenApiPermission.CREATE_CALL_SUMMARIZATION,
  VAULT_WORKFLOW_BROWSER: OpenApiPermission.USE_VAULT_WORKFLOW_BROWSER,
  CREATE_KNOWLEDGE_BASE_PROJECT:
    OpenApiPermission.CREATE_KNOWLEDGE_BASE_PROJECT,
  VIEW_LAST_ACTIVITY_DATE: OpenApiPermission.VIEW_LAST_ACTIVITY_DATE,
  VIEW_DMS_BACKEND_FILE_DOWNLOAD_VAULT:
    OpenApiPermission.VIEW_DMS_BACKEND_FILE_DOWNLOAD_VAULT,
  MODIFY_WORKSPACE_PROJECTS: OpenApiPermission.MODIFY_WORKSPACE_PROJECTS,
  VIEW_VAULT_WORKSPACE_PROJECTS:
    OpenApiPermission.VIEW_VAULT_WORKSPACE_PROJECTS,
  CLIENT_ADMIN_SELF_SERVE_PERMS_READONLY:
    OpenApiPermission.CLIENT_ADMIN_SELF_SERVE_PERMS_READONLY,
  CLIENT_ADMIN_SELF_SERVE_PERMS_WRITE:
    OpenApiPermission.CLIENT_ADMIN_SELF_SERVE_PERMS_WRITE,
  CLIENT_ADMIN_PLAYBOOKS: OpenApiPermission.CLIENT_ADMIN_PLAYBOOKS,
  USE_KNOWLEDGE_BASE_PROJECT: OpenApiPermission.USE_KNOWLEDGE_BASE_PROJECT,
  CREATE_VAULT_API: OpenApiPermission.CREATE_VAULT_API,
  USE_UNIFIED_ASSISTANT: OpenApiPermission.USE_UNIFIED_ASSISTANT,
  VIEW_CAN_USER_REDIRECT: OpenApiPermission.VIEW_CAN_USER_REDIRECT,
  USE_CUSTOM_SIDEBAR_GUIDANCE: OpenApiPermission.USE_CUSTOM_SIDEBAR_GUIDANCE,
  USE_CLAUDE_ENABLED_FEATURES: OpenApiPermission.USE_CLAUDE_ENABLED_FEATURES,
  USE_ASSISTANT_DRAFT_O1: OpenApiPermission.USE_ASSISTANT_DRAFT_O1,
  USE_THINKING_STATES: OpenApiPermission.USE_THINKING_STATES,
  USE_CMD_K: OpenApiPermission.USE_CMD_K,
  USE_WORKSPACE_PERSONALIZATION:
    OpenApiPermission.USE_WORKSPACE_PERSONALIZATION,
  USE_VAULT_FILE_ORGANIZATION_IMPROVEMENTS:
    OpenApiPermission.USE_VAULT_FILE_ORGANIZATION_IMPROVEMENTS,
  USE_INTERNAL_DOCUMENT_DEBUGGER:
    OpenApiPermission.USE_INTERNAL_DOCUMENT_DEBUGGER,
  VIEW_KS_SUGGESTIONS: OpenApiPermission.VIEW_KS_SUGGESTIONS,
  CREATE_USER_DEFINED_WORKFLOWS:
    OpenApiPermission.CREATE_USER_DEFINED_WORKFLOWS,
  CREATE_REVIEW_WORKFLOWS: OpenApiPermission.CREATE_REVIEW_WORKFLOWS,
  USE_4P1_ASSISTANT: OpenApiPermission.USE_4P1_ASSIST,
  USE_4P1_VAULT_REVIEW: OpenApiPermission.USE_4P1_VAULT_REVIEW,
  USE_4P1_WORKFLOWS: OpenApiPermission.USE_4P1_WORKFLOWS,
  USE_4P1_WORD_ADD_IN: OpenApiPermission.USE_4P1_WORD_ADD_IN,
  USE_4P1_CITATIONS: OpenApiPermission.USE_4P1_CITATIONS,
  USE_O3_DRAFTS: OpenApiPermission.USE_O3_DRAFTS,
  USE_EDGAR_OPEN_ENDED: OpenApiPermission.USE_EDGAR_OPENENDED,
} as const
export type Permission = (typeof Permission)[keyof typeof Permission]

type OpenApiPermissionMapping = {
  [K in OpenApiPermission]: boolean
}

export const checkPermissionsCoverage = (
  permissionObject: Record<string, OpenApiPermission>
): OpenApiPermissionMapping => {
  const coverage: Partial<OpenApiPermissionMapping> = {}

  Object.values(OpenApiPermission).forEach((permission) => {
    coverage[permission] = Object.values(permissionObject).includes(permission)
  })

  return coverage as OpenApiPermissionMapping
}

export const SENSITIVE_USER_PERMS: Set<Permission> = new Set(
  Object.values(SensitiveUserPermission) as Permission[]
)

export const SENSITIVE_WORKSPACE_PERMS: Set<Permission> = new Set(
  Object.values(SensitiveUserPermission) as Permission[]
)

export const SENSITIVE_CUSTOMER_DATA_PERMS: Set<Permission> = new Set(
  Object.values(SensitiveCustomerDataPermission) as Permission[]
)

export const INTERNAL_ONLY_PERMS: Set<Permission> = new Set(
  Object.values(InternalOnlyPermission) as Permission[]
)

export const RESTRICTED_PERMS: Set<Permission> = new Set(
  Object.values(RestrictedPermission) as Permission[]
)

export const DEFAULT_BASE_PERMS: Set<Permission> = new Set(
  Object.values(DefaultBasePermission) as Permission[]
)

export const DEFAULT_ADMIN_PERMS: Set<Permission> = new Set(
  Object.values(DefaultAdminPermission) as Permission[]
)

export const SENSITIVE_DATA_PERM_GRANTER_EMAILS: Set<string> = new Set(
  Object.values(SensitiveDataPermGranterEmail)
)

export const INTEGRATION_PERMS: Set<Permission> = new Set(
  Object.values(IntegrationPermission) as Permission[]
)

export const isInternalOnlyPerm = (perm_id: Permission) => {
  return INTERNAL_ONLY_PERMS.has(perm_id)
}

export const isSensitiveUserPerm = (perm_id: Permission) => {
  return SENSITIVE_USER_PERMS.has(perm_id)
}

export const isSensitiveWorkspacePerm = (perm_id: Permission) => {
  return SENSITIVE_WORKSPACE_PERMS.has(perm_id)
}

export const isSensitivePerm = (perm_id: Permission) => {
  return isSensitiveUserPerm(perm_id) || isSensitiveWorkspacePerm(perm_id)
}

export const isRestrictedPerm = (perm_id: Permission) => {
  return RESTRICTED_PERMS.has(perm_id)
}

export const isSensitiveCustomerDataPerm = (perm_id: Permission) => {
  return SENSITIVE_CUSTOMER_DATA_PERMS.has(perm_id)
}

export const isInternalUser = (email: string) => {
  return email.endsWith('@harvey.ai') || email.endsWith('@ext.harvey.ai')
}

export const isIntegrationPerm = (permission: Permission) => {
  return INTEGRATION_PERMS.has(permission)
}

export const API_USER_SENTINEL = 'svc-user'

// TODO(@jhoong) we should use the UserInfo type from the generated openapi code
export interface RawUserInfo {
  dbId: string
  id: string // email
  pseudonymizedId: string
  permissions: Permission[]
  workspace: RawWorkspace
  permsWithSource: PermWithSource[]
  settings?: UserSettings
  deletedAt?: Maybe<string>
  createdAt?: Maybe<string>
  administrableWorkspaces: number[]
  backendVersion?: string
  fetchErr?: Error
  requiresOnboardingNewUser: boolean
  requiresOnboardingExistingUser: boolean
  userProfile: Maybe<UserInfoUserProfile>
}

export class UserInfo {
  dbId: string
  id: string
  pseudonymizedId: string
  permissions: Permission[]
  permsWithSource: PermWithSource[]
  workspace: Workspace
  settings: UserSettings
  deletedAt?: Maybe<string>
  createdAt?: Maybe<string>
  permsByWorkspace?: Record<number, Permission[]>
  backendVersion?: string
  fetchErr?: Error
  requiresOnboardingNewUser: boolean
  requiresOnboardingExistingUser: boolean
  userProfile: Maybe<UserInfoUserProfile>
  administrableWorkspaces: number[]

  constructor(raw: RawUserInfo) {
    this.dbId = raw.dbId
    this.id = raw.id
    this.pseudonymizedId = raw.pseudonymizedId
    this.permissions = raw.permissions
    this.permsWithSource = raw.permsWithSource
    this.workspace = new Workspace(raw.workspace)
    this.settings = raw.settings ?? {}
    this.deletedAt = raw.deletedAt
    this.createdAt = raw.createdAt
    this.backendVersion = raw.backendVersion
    this.fetchErr = raw.fetchErr
    this.requiresOnboardingNewUser = raw.requiresOnboardingNewUser
    this.requiresOnboardingExistingUser = raw.requiresOnboardingExistingUser
    this.userProfile = raw.userProfile
    this.administrableWorkspaces = raw.administrableWorkspaces
  }

  get IsAuthorized(): boolean {
    return (
      this.workspace.id !== -1 &&
      Object.values(Permission).some((p) => this.permissions.includes(p))
    )
  }

  get HasCompletedProductTour(): boolean {
    return this.settings.productTourCompleted ?? false
  }

  get HasCompletedV1WelcomeModal(): boolean {
    return this.settings.v1WelcomeModalCompleted ?? false
  }

  get HasCompletedV1ProductTour(): boolean {
    return this.settings.v1ProductTourCompleted ?? false
  }

  get WelcomeScreens() {
    return this.settings.welcomeScreens
  }

  get WelcomeScreensToShow(): WelcomeScreen[] {
    const userWs = this.settings.welcomeScreens ?? {}
    const wsToShow = this.workspace.welcomeScreens.filter((ws) => {
      // check userWs to see if it has a key matching ws.id
      return !(ws.id in userWs)
    })

    return wsToShow
  }

  get isWorkspacePersonalizationUser(): boolean {
    return this.permissions.includes(Permission.USE_WORKSPACE_PERSONALIZATION)
  }

  get isCustomSidebarGuidanceUser(): boolean {
    return this.permissions.includes(Permission.USE_CUSTOM_SIDEBAR_GUIDANCE)
  }

  get IsInternalUser(): boolean {
    return isInternalUser(this.id)
  }

  get IsTestUser(): boolean {
    return this.id.endsWith('@test.com') || this.id.endsWith('.harvey-test.org')
  }

  get IsBaseUser(): boolean {
    return this.permissions.includes(Permission.BASE)
  }

  get IsReadSensitiveDataUser(): boolean {
    return (
      this.permissions.includes(Permission.READ_SENSITIVE_DATA) &&
      this.IsInternalUser
    )
  }

  get isReadSomeSensitiveDataUser(): boolean {
    return (
      this.permissions.includes(Permission.READ_SOME_SENSITIVE_DATA) &&
      this.IsInternalUser
    )
  }

  get isSensitiveDataReader(): boolean {
    return this.IsReadSensitiveDataUser || this.isReadSomeSensitiveDataUser
  }

  get IsOrganizationAdmin(): boolean {
    return this.permissions.includes(Permission.ORGANIZATION_ADMIN)
  }

  get IsTerritoryAdmin(): boolean {
    return (
      this.IsOrganizationAdmin ||
      this.permissions.includes(Permission.TERRITORY_ADMIN)
    )
  }

  get IsInternalAdminWriter(): boolean {
    return (
      this.IsInternalUser &&
      this.permissions.includes(Permission.INTERNAL_ADMIN_WRITE)
    )
  }

  get IsInternalAdminReader(): boolean {
    return (
      this.IsInternalAdminWriter ||
      (this.IsInternalUser &&
        (this.permissions.includes(Permission.INTERNAL_ADMIN_READ) ||
          this.permissions.includes(Permission.MANAGE_USERS)))
    )
  }

  get IsDocQaUser(): boolean {
    return this.permissions.includes(Permission.DOCUMENT_QA)
  }

  get IsMultiDocQaUser(): boolean {
    return this.permissions.includes(Permission.MULTI_DOC_QA)
  }

  get IsCorpusQaUser(): boolean {
    return this.permissions.includes(Permission.CORPUS_QA)
  }

  get isDocumentUser(): boolean {
    return this.IsDocQaUser || this.IsMultiDocQaUser || this.IsCorpusQaUser
  }

  get IsAssistantUser(): boolean {
    return (
      this.IsAssistantV2User ||
      this.isDocumentUser ||
      this.permissions.includes(Permission.BASE)
    )
  }

  get IsTranslationUser(): boolean {
    return this.permissions.includes(Permission.TRANSLATION)
  }

  get IsDraftingUser(): boolean {
    return this.permissions.includes(Permission.DRAFTING)
  }

  get IsResponseComparisonUser(): boolean {
    return (
      this.permissions.includes(Permission.RESPONSE_COMPARE_USER) ||
      this.permissions.includes(Permission.RESPONSE_COMPARE_ADMIN)
    )
  }

  get IsResponseComparisonAdmin(): boolean {
    return this.permissions.includes(Permission.RESPONSE_COMPARE_ADMIN)
  }

  get IsPWCTaxFeedbackExportUser(): boolean {
    return (
      this.IsInternalUser &&
      this.permissions.includes(Permission.PWC_TAX_FEEDBACK_EXPORT)
    )
  }

  get IsDocumentDebuggerUser(): boolean {
    return this.permissions.includes(Permission.USE_INTERNAL_DOCUMENT_DEBUGGER)
  }

  get IsHistoryUser(): boolean {
    return (
      this.workspace.couldHistory &&
      this.permissions.includes(Permission.HISTORY)
    )
  }

  get IsVaultUser(): boolean {
    return this.permissions.includes(Permission.VAULT_CREATE)
  }

  get IsVaultReviewVerificationUser(): boolean {
    return (
      this.IsVaultUser &&
      this.permissions.includes(Permission.VAULT_REVIEW_VERIFICATION)
    )
  }

  get IsVaultInternalOnlyUser(): boolean {
    return this.permissions.includes(Permission.VAULT_INTERNAL_ONLY_CREATE)
  }

  get IsVaultFileOrganizationImprovementsUser(): boolean {
    return (
      this.permissions.includes(
        Permission.USE_VAULT_FILE_ORGANIZATION_IMPROVEMENTS
      ) || this.permissions.includes(Permission.VAULT_INTERNAL_ONLY_CREATE)
    )
  }

  get IsVaultCreateSandboxProjectUser(): boolean {
    return this.permissions.includes(Permission.VAULT_SANDBOX_PROJECT_CREATE)
  }

  /**
   * User can see "Shared with you" tab in Vault home and view shared projects
   */
  get IsVaultViewSharesUser(): boolean {
    return (
      (this.permissions.includes(Permission.USE_VAULT_SHARING) ||
        this.permissions.includes(Permission.CREATE_VAULT_SHARES)) &&
      this.permissions.includes(Permission.VAULT_CREATE) &&
      this.workspace.sharingSettings.vault.enabled
    )
  }

  get isKsSuggestionsUser(): boolean {
    return this.permissions.includes(Permission.VIEW_KS_SUGGESTIONS)
  }

  /**
   * User can share projects with others in addition to viewing shared projects
   */
  get IsVaultCreateSharesUser(): boolean {
    return (
      this.permissions.includes(Permission.CREATE_VAULT_SHARES) &&
      this.permissions.includes(Permission.VAULT_CREATE) &&
      this.workspace.sharingSettings.vault.enabled
    )
  }

  /** User can share/update shares for events */
  get IsEventCreateSharesUser(): boolean {
    return (
      this.permissions.includes(Permission.CREATE_EVENT_SHARES) &&
      this.workspace.sharingSettings.assistant.enabled
    )
  }

  get IsVaultSharingManagementUser(): boolean {
    return (
      this.permissions.includes(Permission.MANAGE_SHARING) &&
      this.workspace.hasVaultAddOn
    )
  }

  get IsAssistantSharingManagementUser(): boolean {
    return (
      this.permissions.includes(Permission.MANAGE_SHARING) &&
      this.IsAssistantUser
    )
  }

  get IsSharingManagementUser(): boolean {
    return (
      this.IsVaultSharingManagementUser || this.IsAssistantSharingManagementUser
    )
  }

  get IsVaultWorkflowAdminUser(): boolean {
    return (
      this.IsInternalUser &&
      this.IsVaultUser &&
      this.permissions.includes(Permission.VAULT_WORKFLOW_ADMIN)
    )
  }

  get IsVaultCreateReviewWorkflowsUser(): boolean {
    return (
      this.IsVaultUser &&
      this.permissions.includes(Permission.CREATE_REVIEW_WORKFLOWS)
    )
  }

  get IsVaultProjectClientMatterUser(): boolean {
    return (
      this.permissions.includes(Permission.VAULT_PROJECT_CLIENT_MATTER) &&
      this.IsVaultUser
    )
  }

  get IsCreateKnowledgeBaseProjectUser(): boolean {
    return (
      this.permissions.includes(Permission.CREATE_KNOWLEDGE_BASE_PROJECT) &&
      this.IsVaultUser
    )
  }

  get IsUseKnowledgeBaseProjectUser(): boolean {
    return (
      this.permissions.includes(Permission.USE_KNOWLEDGE_BASE_PROJECT) &&
      this.IsVaultUser
    )
  }

  get IsKnowledgeBaseProjectUser(): boolean {
    return (
      this.IsUseKnowledgeBaseProjectUser ||
      this.IsCreateKnowledgeBaseProjectUser
    )
  }

  get IsLibraryUser(): boolean {
    return (
      this.permissions.includes(Permission.LIBRARY_PROMPTS) ||
      this.permissions.includes(Permission.LIBRARY_EXAMPLES) ||
      this.permissions.includes(Permission.LIBRARY_ADMIN)
    )
  }

  get IsLibraryAdmin(): boolean {
    return this.permissions.includes(Permission.LIBRARY_ADMIN)
  }

  get IsLibraryPromptsUser(): boolean {
    return (
      this.permissions.includes(Permission.LIBRARY_PROMPTS) ||
      this.IsLibraryAdmin
    )
  }

  get IsLibraryExamplesUser(): boolean {
    return (
      this.permissions.includes(Permission.LIBRARY_EXAMPLES) ||
      this.IsLibraryAdmin
    )
  }

  get IsLibraryPrivatePromptUser(): boolean {
    return (
      (this.IsLibraryPromptsUser &&
        this.permissions.includes(Permission.LIBRARY_PRIVATE_PROMPTS)) ||
      this.IsLibraryAdmin
    )
  }

  get IsLibraryCreateHarveyItemsUser(): boolean {
    return this.permissions.includes(Permission.LIBRARY_CREATE_HARVEY_ITEMS)
  }

  get nonDbId(): string {
    return this.workspace.isEyesOff ? this.pseudonymizedId : this.id
  }

  get IsEDGARQAUser(): boolean {
    return this.permissions.includes(Permission.SEC_EDGAR_QA)
  }

  get IsRedlinesUser(): boolean {
    return this.permissions.includes(Permission.REDLINES)
  }

  get IsHMRCQAUser(): boolean {
    return this.permissions.includes(Permission.HMRC_QA)
  }

  get IsMemosUser(): boolean {
    return this.permissions.includes(Permission.MEMOS_QA)
  }

  get IsFranceCaseLawUser(): boolean {
    return this.permissions.includes(Permission.FRANCE_CASE_LAW)
  }

  get IsEurLexUser(): boolean {
    return this.permissions.includes(Permission.EURLEX_QA)
  }

  get IsJapanTaxQAUser(): boolean {
    return this.permissions.includes(Permission.JAPAN_TAX_QA)
  }

  get IsKoreaTaxQAUser(): boolean {
    return this.permissions.includes(Permission.KOREA_TAX_QA)
  }

  get IsItalyTaxQAUser(): boolean {
    return this.permissions.includes(Permission.ITALY_TAX_QA)
  }

  get IsKsaTaxQAUser(): boolean {
    return this.permissions.includes(Permission.KSA_TAX_QA)
  }

  get IsHongKongTaxQAUser(): boolean {
    return this.permissions.includes(Permission.HONG_KONG_TAX_QA)
  }

  get IsUAETaxQAUser(): boolean {
    return this.permissions.includes(Permission.UAE_TAX_QA)
  }

  get IsNetherlandsTaxQAUser(): boolean {
    return this.permissions.includes(Permission.NETHERLANDS_TAX_QA)
  }

  get IsIndiaTaxQAUser(): boolean {
    return this.permissions.includes(Permission.INDIA_TAX_QA)
  }

  get IsAustraliaTaxQaUser(): boolean {
    return this.permissions.includes(Permission.AUSTRALIA_TAX_QA)
  }

  get IsCuatrecasasUser(): boolean {
    return this.permissions.includes(Permission.CUATRECASAS)
  }

  get IsFromCounselUser(): boolean {
    return this.permissions.includes(Permission.FROM_COUNSEL)
  }

  get IsPillarTwoTaxQAUser(): boolean {
    return this.permissions.includes(Permission.PILLAR_TWO_TAX_QA)
  }

  get IsMexicoTaxQAUser(): boolean {
    return this.permissions.includes(Permission.MEXICO_TAX_QA)
  }

  get IsRegionalTaxQAUser(): boolean {
    return (
      this.IsHMRCQAUser ||
      this.IsJapanTaxQAUser ||
      this.IsNetherlandsTaxQAUser ||
      this.IsIndiaTaxQAUser ||
      this.IsAustraliaTaxQaUser ||
      this.IsPillarTwoTaxQAUser ||
      this.isIrelandTaxQaUser ||
      this.isSwedenTaxQaUser ||
      this.isSwitzerlandTaxQaUser ||
      this.IsKoreaTaxQAUser ||
      this.IsItalyTaxQAUser ||
      this.IsKsaTaxQAUser ||
      this.IsUAETaxQAUser ||
      this.IsMexicoTaxQAUser ||
      this.IsWWTSQAUser ||
      this.IsHongKongTaxQAUser
    )
  }

  get IsAusBreachReportingUser(): boolean {
    return this.permissions.includes(Permission.AUS_BREACH_REPORTING)
  }

  get IsWWTSQAUser(): boolean {
    return this.permissions.includes(Permission.WWTS_QA)
  }

  get IsCompanyProfileUser(): boolean {
    return this.permissions.includes(Permission.COMPANY_PROFILE)
  }

  get IsFeedbackUser(): boolean {
    return this.permissions.includes(Permission.FEEDBACK)
  }

  get IsInternetBrowsingUser(): boolean {
    return this.permissions.includes(Permission.INTERNET_BROWSING)
  }

  get IsOGCReviewUser(): boolean {
    return this.permissions.includes(Permission.OGC_REVIEW)
  }

  get IsDocumentComparisonUser(): boolean {
    return this.permissions.includes(Permission.DOC_COMPARE)
  }

  get IsAssistantDraftUser(): boolean {
    return this.permissions.includes(Permission.ASSISTANT_DRAFT)
  }

  get IsAssistantV2User(): boolean {
    return this.permissions.includes(Permission.USE_ASSISTANT_V2)
  }

  get IsUnifiedAssistantUser(): boolean {
    return this.permissions.includes(Permission.USE_UNIFIED_ASSISTANT)
  }

  get IsAssistantV2SurveyUser(): boolean {
    return this.permissions.includes(
      Permission.CREATE_ASSISTANT_V2_SURVEY_FEEDBACK
    )
  }

  get canSeeLastActiveAt(): boolean {
    return this.permissions.includes(Permission.VIEW_LAST_ACTIVITY_DATE)
  }

  get IsHarveyV1ResearchUser(): boolean {
    // Harvey employees should have access to Research, and the assumption is that they will also have access to any underlying products
    // External people with access to Tax Q&A should also have access to Research (this is a small group right now)
    // No external people should have access to SEC in Research
    return (
      this.IsRegionalTaxQAUser ||
      this.permissions.includes(Permission.SEC_EDGAR_QA) ||
      this.permissions.includes(Permission.MEMOS_QA) ||
      this.permissions.includes(Permission.EURLEX_QA) ||
      this.permissions.includes(Permission.USA_CASE_LAW) ||
      this.permissions.includes(Permission.FRANCE_CASE_LAW) ||
      this.permissions.includes(Permission.AUS_BREACH_REPORTING) ||
      this.permissions.includes(Permission.CUATRECASAS) ||
      this.permissions.includes(Permission.FROM_COUNSEL) ||
      this.permissions.includes(Permission.USE_SINGAPORE_CASE_LAW) ||
      this.permissions.includes(Permission.USE_SWEDEN_CASE_LAW) ||
      this.permissions.includes(Permission.USE_LEFEBVRE_KS) ||
      this.permissions.includes(Permission.USE_BREDIN_PRAT_KS)
    )
  }

  get isIrelandTaxQaUser(): boolean {
    return this.permissions.includes(Permission.IRELAND_TAX_QA)
  }

  get isSwedenTaxQaUser(): boolean {
    return this.permissions.includes(Permission.SWEDEN_TAX_QA)
  }

  get isSwitzerlandTaxQaUser(): boolean {
    return this.permissions.includes(Permission.SWITZERLAND_TAX_QA)
  }

  get IsHarveyV1WorkflowUser(): boolean {
    // true if the user has permission for any workflows
    const WorkflowPermissions = [
      Permission.COMPANY_PROFILE,
      Permission.CONTRACTS,
      Permission.DILIGENCE_TRANSCRIPTS,
      Permission.DOC_COMPARE,
      Permission.DRAFTING,
      Permission.OGC_REVIEW,
      Permission.PWC_DEALS,
      Permission.REDLINES,
      Permission.TRANSCRIPTS,
      Permission.TRANSLATION,
    ]

    return WorkflowPermissions.some((perm) => this.permissions.includes(perm))

    // XXX: Something funky going on w/ module resolution here, TBD
    // return Object.values(WorkflowTypeToDetails()).some((workflow) =>
    //   this.permissions.includes(workflow.permission as Permission)
    // )
  }

  get IsPwcUser(): boolean {
    const emailDomains = [
      'pwc.com',
      'hk.pwc.com',
      'cn.pwc.com',
      'au.pwc.com',
      'pwc.ch',
      'tiangandpartners.com',
      'avocats.pwc.com',
      'pwc.lu',
      'pwclegal.lu',
      'lhlaw.com.br',
      'pwclegal.mu',
      'david-baias.ro',
      'pwc.onmicrosoft.com',
    ]

    if (this.workspace.slug) {
      return this.workspace.slug.startsWith('pwc')
    } else {
      const thisEmailDomain = this.id.split('@')[1]
      return emailDomains.includes(thisEmailDomain)
    }
  }

  get isUserManagement(): boolean {
    return (
      this.IsInternalUser &&
      (this.permissions.includes(Permission.MANAGE_USERS) ||
        this.IsInternalAdminWriter)
    )
  }

  get isUserInspector(): boolean {
    return (
      this.IsInternalUser &&
      this.permissions.includes(Permission.VIEW_USER_INSPECTOR)
    )
  }

  get isUserManagementTabVisible(): boolean {
    return this.IsInternalAdminReader || this.isUserInspector
  }

  get isReadEngInternal(): boolean {
    return (
      this.IsInternalUser &&
      this.permissions.includes(Permission.READ_ENG_INTERNAL)
    )
  }

  get isWriteEngInternal(): boolean {
    return (
      this.IsInternalUser &&
      this.permissions.includes(Permission.WRITE_ENG_INTERNAL)
    )
  }

  get isClientMattersReadUser(): boolean {
    return (
      this.permissions.includes(Permission.CLIENT_MATTERS_READ) ||
      this.permissions.includes(Permission.CLIENT_MATTERS_WRITE)
    )
  }

  get isClientMattersWriteUser(): boolean {
    return this.permissions.includes(Permission.CLIENT_MATTERS_WRITE)
  }

  get isClientMattersManagementUser(): boolean {
    return (
      this.isClientMattersWriteUser ||
      (this.IsTerritoryAdmin && this.isClientMattersReadUser) ||
      this.isUserManagement
    )
  }

  get isContractsUser(): boolean {
    return this.permissions.includes(Permission.CONTRACTS)
  }

  get isTranscriptsUser(): boolean {
    return this.permissions.includes(Permission.TRANSCRIPTS)
  }

  get isDiligenceUser(): boolean {
    return this.permissions.includes(Permission.PWC_DEALS)
  }

  get isCompetitiveAnalysisUser(): boolean {
    return this.permissions.includes(Permission.USE_COMPETITIVE_ANALYSIS)
  }

  get isDiligenceTranscriptsUser(): boolean {
    return this.permissions.includes(Permission.DILIGENCE_TRANSCRIPTS)
  }

  get isDealsDiscoveryAsyncUser(): boolean {
    return this.permissions.includes(Permission.DEALS_DISCOVERY_ASYNC)
  }

  get isDealsDiscoveryFromVaultUser(): boolean {
    return this.permissions.includes(Permission.DEALS_DISCOVERY_FROM_VAULT)
  }

  get isSkipRateLimitUser(): boolean {
    return this.permissions.includes(Permission.USESKIP_RATE_LIMIT)
  }

  get canSeeSaveExample(): boolean {
    return !!this.IsLibraryExamplesUser && !!this.IsLibraryAdmin
  }

  // gated on having history perm
  get IsWorkspaceHistoryReader(): boolean {
    return (
      this.permissions.includes(Permission.READ_WORKSPACE_HISTORY) &&
      this.permissions.includes(Permission.HISTORY)
    )
  }

  get IsClientAdminAddUsers(): boolean {
    return this.permissions.includes(Permission.CLIENT_ADMIN_ADD_USERS)
  }

  get IsClientAdminRemoveUsers(): boolean {
    return this.permissions.includes(Permission.CLIENT_ADMIN_REMOVE_USERS)
  }

  get IsClientAdminViewUsers(): boolean {
    return this.permissions.includes(Permission.CLIENT_ADMIN_VIEW_USERS)
  }

  get IsClientAdminSelfServePermsViewer(): boolean {
    return (
      this.permissions.includes(
        Permission.CLIENT_ADMIN_SELF_SERVE_PERMS_READONLY
      ) ||
      this.permissions.includes(Permission.CLIENT_ADMIN_SELF_SERVE_PERMS_WRITE)
    )
  }

  get IsClientAdminSelfServePermsWriter(): boolean {
    return this.permissions.includes(
      Permission.CLIENT_ADMIN_SELF_SERVE_PERMS_WRITE
    )
  }

  get isSettingsTabVisible(): boolean {
    return (
      this.IsInternalAdminReader ||
      this.IsWorkspaceInfoViewer ||
      this.isClientMattersReadUser ||
      this.IsWorkspaceHistoryReader ||
      this.IsClientAdminViewUsers ||
      this.IsUsageDashboardViewer ||
      this.IsUsageDashboardV2Viewer ||
      this.IsSharingManagementUser ||
      this.isIntegrationAdmin ||
      this.isAnyIntegrationUser ||
      this.IsClientAdminSelfServePermsViewer ||
      this.isCreateUserProfilesUser ||
      this.isWorkspacePersonalizationUser ||
      this.isVaultWorkspaceProjectsViewer ||
      this.isPlaybookAdminUser
    )
  }

  get IsWorkspaceInfoViewer(): boolean {
    return this.permissions.includes(Permission.VIEW_WORKSPACE_INFO)
  }

  get IsUsageDashboardViewer(): boolean {
    return this.permissions.includes(Permission.VIEW_USAGE_DASHBOARD)
  }

  get IsUsageDashboardV2Viewer(): boolean {
    return this.permissions.includes(Permission.VIEW_USAGE_DASHBOARD_V2)
  }

  get IsDeleteWorkspaceHistoryUser(): boolean {
    return (
      this.permissions.includes(Permission.DELETE_WORKSPACE_HISTORY) &&
      this.IsHistoryUser
    )
  }

  get IsAssistantTaxKnowledgeSourceUser(): boolean {
    return (
      this.permissions.includes(Permission.ASSISTANT_TAX_KNOWLEDGE_SOURCE) ||
      this.permissions.includes(Permission.ASSISTANT_RESEARCH_KNOWLEDGE_SOURCE)
    )
  }

  get isDiscoverTabUser(): boolean {
    return this.permissions.includes(Permission.USE_DISCOVER_TAB)
  }

  get isDeleteUserHistoryUser(): boolean {
    return (
      this.permissions.includes(Permission.DELETE_USER_HISTORY) &&
      this.IsHistoryUser
    )
  }

  get IsDeleteWorkspaceHistoryBulkUser(): boolean {
    return this.permissions.includes(Permission.DELETE_WORKSPACE_HISTORY_BULK)
  }

  get IsViewHelpCenterUser(): boolean {
    return this.permissions.includes(Permission.VIEW_HELP_CENTER)
  }

  get IsAssistantResearchKSUser(): boolean {
    return (
      this.IsHarveyV1ResearchUser &&
      this.permissions.includes(Permission.ASSISTANT_RESEARCH_KNOWLEDGE_SOURCE)
    )
  }

  get IsSwedenCaseLawUser(): boolean {
    return this.permissions.includes(Permission.USE_SWEDEN_CASE_LAW)
  }

  get IsMacfarlanesUser(): boolean {
    return this.permissions.includes(Permission.USE_MACFARLANES_KS)
  }

  get IsSingaporeCaseLawUser(): boolean {
    return this.permissions.includes(Permission.USE_SINGAPORE_CASE_LAW)
  }

  get IsLefebvreUser(): boolean {
    return this.permissions.includes(Permission.USE_LEFEBVRE_KS)
  }

  get IsBredinPratUser(): boolean {
    return this.permissions.includes(Permission.USE_BREDIN_PRAT_KS)
  }

  get permissionsByWorkspace(): Record<number, Permission[]> {
    if (this.permsByWorkspace) {
      return this.permsByWorkspace
    }
    const permsByWorkspace: Record<number, Permission[]> = {}
    this.permsWithSource.forEach((perm) => {
      if (!permsByWorkspace[perm.workspaceId]) {
        permsByWorkspace[perm.workspaceId] = []
      }
      permsByWorkspace[perm.workspaceId].push(perm.permId)
    })
    this.permsByWorkspace = permsByWorkspace
    return permsByWorkspace
  }

  get vaultFeature(): WorkspaceFeature | undefined {
    if (this.IsVaultUser) {
      return WorkspaceFeature.VAULT_ADD_ON
    }
    return undefined
  }

  get isDisallowClientMattersUser(): boolean {
    return (
      this.permissions.includes(Permission.DISALLOW_CLIENT_MATTERS) &&
      this.isClientMattersReadUser
    )
  }

  get isTopClientMattersUser(): boolean {
    return this.permissions.includes(Permission.VIEW_TOP_CLIENT_MATTERS)
  }

  get isIntegrationAdmin(): boolean {
    return this.permissions.includes(Permission.INTEGRATION_ADMIN)
  }

  get isAnyIntegrationUser(): boolean {
    return this.permissions.some((perm) => isIntegrationPerm(perm))
  }

  get isCreateUserProfilesUser(): boolean {
    return this.permissions.includes(Permission.CREATE_USER_PROFILES)
  }

  get isCreateUserProfilesOptionalUser(): boolean {
    return this.permissions.includes(Permission.CREATE_USER_PROFILES_OPTIONAL)
  }

  get isCreateUserProfilesOptionalStepsUser(): boolean {
    return this.permissions.includes(
      Permission.CREATE_USER_PROFILES_OPTIONAL_STEPS
    )
  }

  get isWordAddInUser(): boolean {
    return this.permissions.includes(Permission.USE_HARVEY_FOR_WORD)
  }

  get isPlaybookReviewUser(): boolean {
    return this.permissions.includes(Permission.PLAYBOOK_REVIEW)
  }

  get canViewWordAddInAnnouncement(): boolean {
    return this.permissions.includes(Permission.VIEW_WORD_ADD_IN_ANNOUNCEMENT)
  }

  get canViewGdriveAnnouncement(): boolean {
    return this.permissions.includes(Permission.VIEW_GDRIVE_ANNOUNCEMENT)
  }

  get hasNetdocsExperiment(): boolean {
    return this.permissions.includes(Permission.USE_NETDOCS_EXTENSION)
  }

  get isNewAssistantComposerUser(): boolean {
    return this.permissions.includes(Permission.USE_ASSISTANT_COMPOSER)
  }

  get isCmdKUser(): boolean {
    return this.permissions.includes(Permission.USE_CMD_K)
  }

  get acceptedWorkspaceNotice(): Maybe<AcceptedWorkspaceNotice> {
    return this.settings.acceptedWorkspaceNotice
  }

  get isPlaybookAdminUser(): boolean {
    return this.permissions.includes(Permission.CLIENT_ADMIN_PLAYBOOKS)
  }

  UpdateWorkspaceAndPermissions(
    workspace: Workspace,
    userInfo: Maybe<UserInfo>
  ) {
    if (!userInfo?.isSensitiveDataReader) {
      // read sensitive users get their original perms for any workspace selected
      this.permissions = _.uniq(this.permissionsByWorkspace[workspace.id] ?? []) // if no perms for workspace, set to empty array
    }
    this.workspace = workspace
  }

  GetHelpPanel(taskType: TaskType): Maybe<WelcomeScreen> {
    return this.WelcomeScreensToShow.find((data: any) => {
      return (
        data.type === WelcomeScreenType.HELP_PANEL &&
        // TODO data.taskType not a boolean and should not be negated
        // eslint-disable-next-line
        (!data?.taskType || data?.taskType === taskType)
      )
    })
  }

  GetInterstitial(): Maybe<WelcomeScreen> {
    return this.WelcomeScreensToShow.find((data: any) => {
      return data.type === WelcomeScreenType.INTERSTITIAL
    })
  }

  GetDisallowedClientMatterInterstitial(): Maybe<WelcomeScreen> {
    return this.WelcomeScreensToShow.find((data: any) => {
      return (
        data.type === WelcomeScreenType.DISALLOWED_CLIENT_MATTER_INTERSTITIAL
      )
    })
  }

  get RequiresOnboardingNewUser(): boolean {
    return (
      (this.requiresOnboardingNewUser ?? false) && this.isCreateUserProfilesUser
    )
  }

  get RequiresOnboardingExistingUser(): boolean {
    return (
      (this.requiresOnboardingExistingUser ?? false) &&
      this.isCreateUserProfilesUser
    )
  }

  get isVaultWorkspaceProjectsViewer(): boolean {
    return (
      this.permissions.includes(Permission.VIEW_VAULT_WORKSPACE_PROJECTS) ||
      this.isWorkspaceProjectsModifier
    )
  }

  get isWorkspaceProjectsAdminViewer(): boolean {
    return this.permissions.includes(Permission.READ_WORKSPACE_VAULT)
  }

  get isWorkspaceProjectsModifier(): boolean {
    return this.permissions.includes(Permission.MODIFY_WORKSPACE_PROJECTS)
  }

  get isDmsBackendFileDownloadVaultUser(): boolean {
    return this.permissions.includes(
      Permission.VIEW_DMS_BACKEND_FILE_DOWNLOAD_VAULT
    )
  }
}

const providerId = function (user: User | undefined): string | undefined {
  const subParts = user?.sub?.split('|')
  return subParts?.[subParts.length - 1]?.trim().toLowerCase()
}

export const userId = function (user: User | undefined): string | undefined {
  let userId = user?.email?.trim().toLowerCase()
  if (_.isNil(userId)) {
    userId = providerId(user)
  }

  return userId
}

export const slugFromEmailDomain = function (
  email: string | undefined
): string {
  // email can be undefined, null, or empty.
  if (!email) {
    return ''
  }

  const domain = email.split('@')[1]
  const slug = domain.split('.')[0]
  return slug
}

// TODO: deprecate this
export function CreateUserInfoLite(
  user: User | undefined,
  exception: Error | undefined
): UserInfo {
  const id = userId(user) ?? ''

  return new UserInfo({
    dbId: '',
    id,
    pseudonymizedId: id,
    permissions: [],
    permsWithSource: [],
    workspace: new Workspace({
      id: -1,
      slug: slugFromEmailDomain(id),
      domains: [],
      kind: WorkspaceKind.ORGANIZATION,
      friendlyName: '',
      clientName: '',
      eyesOffTier: WorkspaceEyesOffTier.EYES_OFF_BASIC,
    }),
    settings: {},
    fetchErr: exception,
    requiresOnboardingNewUser: false,
    requiresOnboardingExistingUser: false,
    userProfile: null,
    administrableWorkspaces: [],
  })
}

export async function FetchUserInfo(user?: User): Promise<UserInfo> {
  return Services.Backend.Get<RawUserInfo>('user_info', { throwOnError: true })
    .then((response) => new UserInfo(response))
    .then((userInfo) => {
      // Fetch the user profile to update the state
      useUserProfileStore.getState().setUserProfileFromUserInfo(userInfo)
      return userInfo
    })
    .catch((initialError) => {
      // If the first call fails, try the fallback endpoint '/profile_md'
      return Services.Backend.Get<RawUserInfo>('profile_md', {
        throwOnError: true,
      })
        .then((response) => new UserInfo(response))
        .then((userInfo) => {
          // If the fallback succeeds, update the profile store and return the user info
          useUserProfileStore.getState().setUserProfileFromUserInfo(userInfo)
          return userInfo
        })
        .catch((fallbackError) => {
          // If both calls fail, proceed with the original error handling
          Services.HoneyComb.Record({
            metric: 'frontend.auth.user_info_fetch_failed',
            error: 'Failed to fetch user info from backend after retry',
            initial_error: initialError?.message,
            fallback_error: fallbackError?.message,
            user_id: user?.email,
            user_raw_id: user?.sub,
            user_nickname: user?.nickname,
          })
          // Print out some debugging info so that it's a little more clear
          // why things failed.
          if (!user?.email) {
            console.warn('Auth email is empty. Please contact support.')
          }
          // Use the initial error for creating the lite user info
          return CreateUserInfoLite(user, initialError)
        })
    })
}

export const acknowledgeCustomNotice = async (
  noticeId: string,
  acceptedAt: string
): Promise<void> => {
  return Services.Backend.Post<void>(
    `user_settings/accept_notice`,
    { noticeId, acceptedAt },
    { throwOnError: true }
  )
}
